import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Skeleton, Tooltip } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import BrandAvatar from "../../Component/Avatar/Avatar";
import { RBI_COLORS, RBI_WEIGHTS } from "../../utils/brandIndexCalc";
import { ENTITY_LABELS } from "../../utils/labelMapping";

// ─── Constants ────────────────────────────────────────────────────────────────
// A function, not a static array, so labels re-resolve from ENTITY_LABELS on every render
// (a module-level array would freeze on whichever customer's labels loaded first).
function getMetrics() {
  return [
    { key: "visibility",       label: ENTITY_LABELS.rank,             weight: RBI_WEIGHTS.visibility,       color: RBI_COLORS.visibility,       rawMax: 50  },
    { key: "sentiment",        label: "Sentiment",                    weight: RBI_WEIGHTS.sentiment,        color: RBI_COLORS.sentiment,        rawMax: 50  },
    { key: "brandPillars",     label: ENTITY_LABELS.brandPillar,      weight: RBI_WEIGHTS.brandPillars,     color: RBI_COLORS.brandPillars,     rawMax: 10  },
    { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria, weight: RBI_WEIGHTS.purchaseCriteria, color: RBI_COLORS.purchaseCriteria, rawMax: 10  },
  ];
}

const TOOLTIP_SX = {
  bgcolor: "#1e2340", fontSize: "0.73rem", borderRadius: "10px",
  padding: "9px 12px", boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
  "& .MuiTooltip-arrow": { color: "#1e2340" },
};

// Average across LLM/market/persona entries (same logic as in CategoryScoreCardsNew)
function avgEntry(entries) {
  const valid = (entries || []).filter(e => e.total != null);
  if (!valid.length) return null;
  const avg = (fn) => valid.reduce((s, e) => s + (fn(e) ?? 0), 0) / valid.length;
  return {
    ...valid[0],
    total:               avg(e => e.total),
    rank:                avg(e => e.rank),
    favorability:        avg(e => e.favorability),
    brandPillarsAvg:     avg(e => e.brandPillarsAvg),
    purchaseCriteriaAvg: avg(e => e.purchaseCriteriaAvg),
    contributions: {
      visibility:       avg(e => e.contributions?.visibility),
      sentiment:        avg(e => e.contributions?.sentiment),
      brandPillars:     avg(e => e.contributions?.brandPillars),
      purchaseCriteria: avg(e => e.contributions?.purchaseCriteria),
    },
  };
}

// ─── Single Metric Card ───────────────────────────────────────────────────────

function MetricCard({ metric, contribution, rawScore, normScore, isProduct }) {
  const label = isProduct && metric.key === "brandPillars" ? "Product Dimensions" : metric.label;
  const num = Number(rawScore);
  const rawDisplay =
    rawScore != null
      ? num % 1 === 0
        ? String(num)
        : num.toFixed(1)
      : "—";
  const contribDisplay = contribution != null ? Number(contribution) % 1 === 0
    ? String(Number(contribution)) : Number(contribution).toFixed(1) : "—";
  const normDisplay = normScore != null ? Number(normScore).toFixed(1) : "—";

  return (
    <Tooltip arrow placement="top"
      componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
      title={
        <div style={{ minWidth: 185 }}>
          <div style={{ fontWeight: 600, fontSize: "0.76rem", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: metric.color, display: "inline-block", flexShrink: 0 }} />
            {label}
          </div>
          <div style={{ fontSize: "0.7rem", display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <span style={{ opacity: 0.75 }}>Raw Score</span>
              <strong>{rawDisplay}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <span style={{ opacity: 0.75 }}>Normalized</span>
              <strong>{normDisplay}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <span style={{ opacity: 0.75 }}>Weight</span>
              <strong>{Math.round(metric.weight * 100)}%</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, borderTop: "1px solid rgba(255,255,255,0.15)", marginTop: 3, paddingTop: 3 }}>
              <span style={{ opacity: 0.75 }}>Contribution</span>
              <strong style={{ color: metric.color }}>{contribDisplay}</strong>
            </div>
          </div>
        </div>
      }
    >
      <div style={{
        flex: 1,
        background: "#f4f6fb",
        borderRadius: 10,
        padding: "12px 6px 11px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
        cursor: "default",
      }}>


        <div style={{ lineHeight: 1 }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: RBI_COLORS[metric.key] }}>{rawDisplay}</span>
          <span style={{ fontSize: 12, fontWeight: 400, color: "#8a94a6" }}>/{metric.rawMax}</span>
        </div>

        <div style={{ width: "20%", height: 1, backgroundColor: "#d9dee7", margin: "5px 0" }} />


        <span style={{ fontSize: "0.85rem", color: "#5a6478", marginTop: 4, whiteSpace: "nowrap", fontWeight: 500 }}>
          {contribDisplay}
        </span>
      </div>
    </Tooltip>
  );
}

// ─── Brand Row ────────────────────────────────────────────────────────────────

function BrandMetricRow({ brand, entry, onInfoClick }) {
  const METRICS = getMetrics();
  if (!entry) {
    return (
      <div style={rowStyle}>
        <div style={avatarCellStyle}><BrandAvatar domainName={brand.domain || "example.com"} brandName={brand.name} /></div>
        {METRICS.map(m => (
          <div key={m.key} style={{ flex: 1, height: 56, borderRadius: 10, overflow: "hidden" }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </div>
        ))}
        <div style={rbiCellStyle}><Skeleton variant="text" width={30} /></div>
      </div>
    );
  }

  const isProduct = entry.isProduct;
  const rawByKey = {
    visibility:       entry.rank,
    sentiment:        entry.favorability,
    brandPillars:     entry.brandPillarsAvg,
    purchaseCriteria: entry.purchaseCriteriaAvg,
  };

  return (
    <div style={rowStyle}>
      <div style={avatarCellStyle}>
        <BrandAvatar domainName={brand.domain || "example.com"} brandName={brand.name} />
      </div>
      {METRICS.map(m => (
        <MetricCard
          key={m.key}
          metric={m}
          contribution={entry.contributions?.[m.key]}
          rawScore={rawByKey[m.key]}
          normScore={entry[`${m.key === "visibility" ? "vis" : m.key === "sentiment" ? "sent" : m.key === "brandPillars" ? "pillars" : "purchase"}Norm`]}
          isProduct={isProduct}
        />
      ))}
      <div style={rbiCellStyle}>
        <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#2d3748" }}>
          {entry.total != null ? Math.round(entry.total) : "—"}
        </span>
        <button onClick={() => onInfoClick(brand)} style={infoBtnStyle} title="View details">
          <InfoOutlinedIcon style={{ fontSize: 14 }} />
        </button>
      </div>
    </div>
  );
}

// ─── Shared Styles ────────────────────────────────────────────────────────────

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "5px 0",
  borderBottom: "1px solid #f0f3fa",
};

const avatarCellStyle = {
  flexShrink: 0,
  width: 38,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const rbiCellStyle = {
  flexShrink: 0,
  width: 52,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
};

const infoBtnStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "2px 3px",
  color: "#8a94a6",
  display: "flex",
  alignItems: "center",
  borderRadius: 4,
  transition: "color 0.15s",
};

// ─── Legend Header ────────────────────────────────────────────────────────────

function LegendHeader({ isProduct }) {
  const METRICS = getMetrics();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 0 8px", borderBottom: "2px solid #edf0f7", marginBottom: 4 }}>
      <div style={{ width: 38, flexShrink: 0 }} />
      {METRICS.map(m => {
        const label = isProduct && m.key === "brandPillars" ? "Product Dimensions" : m.label;
        return (
          <div key={m.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color, flexShrink: 0, display: "inline-block" }} />
              <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#4a5568", whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
            <span style={{ fontSize: "0.63rem", color: "#8a94a6" }}>{Math.round(m.weight * 100)}%</span>
          </div>
        );
      })}
      <div style={{ width: 52, flexShrink: 0, textAlign: "center" }}>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#4a5568" }}>RBI</span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RBIMetricCardsView({ brands, rbiContributionMap, onInfoClick, isCompareMode }) {
  const [visibleCount, setVisibleCount] = useState(5);
  useEffect(() => { setVisibleCount(5); }, [isCompareMode, brands.length]);

  const sorted = useMemo(() => {
    return [...brands].sort((a, b) => {
      const getTotal = (brand) => {
        const entries = rbiContributionMap[brand.name] || [];
        const valid = entries.filter(e => e.total != null);
        return valid.length ? valid.reduce((s, e) => s + e.total, 0) / valid.length : -1;
      };
      return getTotal(b) - getTotal(a);
    });
  }, [brands, rbiContributionMap]);

  const isProduct = useMemo(() => {
    for (const brand of brands) {
      const entries = rbiContributionMap[brand.name] || [];
      if (entries.length && entries[0]?.isProduct != null) return entries[0].isProduct;
    }
    return false;
  }, [brands, rbiContributionMap]);

  const visibleBrands = sorted.slice(0, visibleCount);
  const hasMore = sorted.length > visibleCount;

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #edf0f7" }}>
      <LegendHeader isProduct={isProduct} />
      {visibleBrands.map(brand => {
        const entries = rbiContributionMap[brand.name] || [];
        const entry = isCompareMode ? avgEntry(entries) : (entries[0] ?? null);
        return (
          <BrandMetricRow
            key={brand.name}
            brand={brand}
            entry={entry}
            onInfoClick={onInfoClick}
          />
        );
      })}
      {hasMore && (
        <button
          onClick={() => setVisibleCount(c => c + 5)}
          style={{ marginTop: 8, width: "100%", padding: "6px", background: "none", border: "1px dashed #d0d7e8", borderRadius: 8, color: "#6b7a9d", fontSize: "0.75rem", cursor: "pointer", fontWeight: 600 }}
        >
          Load more ({sorted.length - visibleCount} remaining)
        </button>
      )}
    </div>
  );
}
